import type { FileStorageClient } from '@quromedical/storage';
import { kebabcase } from '@quromedical/utils';
import type { BundleEntry, ResourceType } from '../types';

import { allowedExtensionTypeMap, type ContentType } from './content-type';
import { createFhirDate, createFhirDateTime } from './date';

export class MediaUtils {
  private readonly uriRegex: RegExp;
  constructor(private readonly storageClient: FileStorageClient) {
    this.uriRegex = /[^a-zA-Z0-9-_.]/g;
  }

  getKeyFromUrl = (url: string): string => {
    if (url.startsWith('https://') || url.startsWith('http://')) {
      const [, ...parts] = url
        .replace('https://', '')
        .replace('http://', '')
        .split('/');

      return parts.join('/');
    }

    return url;
  };

  replaceMediaUrl = (media: fhir4.Media, url?: string): fhir4.Media => {
    const content: fhir4.Attachment = { ...media.content, url };
    return { ...media, content };
  };

  populateMediaUrl = async (resource: fhir4.Media): Promise<fhir4.Media> => {
    const { content } = resource;
    const key = content.url
      ? this.storageClient.getKeyFromUrl(content.url)
      : undefined;
    const signedURL = key
      ? await this.storageClient.getSignedURL(key)
      : undefined;
    const updatedResource = this.replaceMediaUrl(resource, signedURL);

    return updatedResource;
  };

  populateSignedUrl = async (
    entry: BundleEntry<fhir4.Media>,
  ): Promise<BundleEntry<fhir4.Media>> => {
    const { resource } = entry;
    const updatedResource = await this.populateMediaUrl(resource);
    return { ...entry, resource: updatedResource };
  };

  replaceDataWithURL = (media: fhir4.Media, url: string): fhir4.Media => {
    const { data: _, ...rest } = media.content;
    const content: fhir4.Attachment = { ...rest, url };

    return { ...media, content };
  };

  /**
   *
   * @param title title for the file
   * @param contentType MIME content type for the file
   * @param patientId ID for the patient that that the file is about
   * @param fileResourceType Resource Type that the file is linked to
   * @param extension use an extension in place of the content type derivation, e.g. `pdf` or `xml`
   * @param date the `Date` for the directory under which the file should be stored. Defaults to `now`
   *
   * @returns Path to the file in the form of
   * `patient/${patientId}/${date}/${dateTime}-${resource-type}-${extension | content-type extension}`
   */
  createKey = (
    title: string,
    // this should be updated to use ContentType but currently it's breaking too many references
    contentType: string,
    patientId: string,
    fileResourceType: ResourceType = 'Media',
    extension?: string,
    date = new Date(),
  ) => {
    const resourceLabel = kebabcase(fileResourceType);

    const resolvedExtension =
      extension || allowedExtensionTypeMap[contentType as ContentType];

    const fhirDate = createFhirDate(date);
    const fhirDateTime = createFhirDateTime(date);

    const sanitizedTitle = title.replace(this.uriRegex, '');

    const key = `patient/${patientId}/${fhirDate}/${fhirDateTime}-${resourceLabel}-${sanitizedTitle}.${resolvedExtension}`;
    return key;
  };

  /**
   * Updates the data for a media and replaces it a url derived from the key
   */
  setMediaUrl = (key: string, initial: fhir4.Media): fhir4.Media => {
    const url = this.storageClient.getFullFileUrl(key);
    const media = this.replaceDataWithURL(initial, url);

    return media;
  };
}
